import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FileText, Video, HelpCircle, BookOpen, Search, ArrowRight, MapPin, Heart, Star, Eye, Download, Monitor, Tablet, Smartphone, Mail, Phone, Globe } from 'lucide-react';
import SEO from '@/components/SEO';
import { triggerInstall, onInstallAvailable } from '@/components/PWAInstallPrompt';

const PatientsHomePage = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [latestArticles, setLatestArticles] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [visibleCards, setVisibleCards] = useState(new Set());
  const [installAvailable, setInstallAvailable] = useState(false);
  const [installing, setInstalling] = useState(false);
  const cardsRef = useRef([]);
  const isStandalone = window.matchMedia && window.matchMedia('(display-mode: standalone)').matches;
  const showPWA = !isStandalone;

  useEffect(() => {
    const unsub = onInstallAvailable((available) => setInstallAvailable(available));
    return () => unsub();
  }, []);

  const handleInstall = async () => {
    setInstalling(true);
    if (installAvailable) await triggerInstall('patient');
    setTimeout(() => setInstalling(false), 2000);
  };

  useEffect(() => {
    fetchCategories();
    fetchLatestArticles();
  }, []);

  // Staggered card entrance animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setVisibleCards(prev => new Set([...prev, entry.target.dataset.cardIndex]));
          }
        });
      },
      { threshold: 0.2 }
    );
    cardsRef.current.forEach(el => { if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, [categories]);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/patient-categories`);
      if (response.ok) setCategories(await response.json());
    } catch (e) { console.error(e); }
  };

  const fetchLatestArticles = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/patient-articles?status=published&access_level=public`);
      if (response.ok) {
        const data = await response.json();
        setLatestArticles(data.slice(0, 6));
      }
    } catch (e) { console.error(e); }
  };

  const supportCards = [
    { icon: Heart, title: "Conseils d'utilisation", desc: "Vous êtes un nouveau porteur ? Pour mieux gérer vos lentilles au quotidien, retrouvez nos conseils", color: '#04a4ec', bgLight: '#e7f5fd', slug: 'conseils', cta: 'Découvrir les conseils' },
    { icon: BookOpen, title: "En savoir +", desc: "Retrouvez des informations et des explications sur les lentilles de contact, leur entretien, et différentes pathologies", color: '#1489f6', bgLight: '#e8f1fe', slug: 'en-savoir-plus', cta: 'Découvrir les articles' },
    { icon: HelpCircle, title: "Foire aux questions", desc: "Les questions les plus fréquentes, auxquelles nous avons déjà répondu, pourront vous aider", color: '#045474', bgLight: '#dceaf0', slug: 'faq', cta: 'Découvrir les réponses', isFaq: true },
    { icon: Video, title: "Tutoriels vidéos", desc: "Pour vous accompagner dans vos gestes du quotidien, retrouvez nos tutoriels vidéos", color: '#b5d37b', bgLight: '#f1f7df', slug: 'tutoriels-videos', cta: 'Découvrir les tutoriels' },
  ];

  return (
    <div className="min-h-screen" style={{ fontFamily: "'Roboto', sans-serif", background: '#ffffff' }} data-testid="patients-home">
      <SEO
        title="Espace Patients - Lentilles de contact"
        description="Conseils, tutoriels et informations pour les porteurs de lentilles de contact. Trouvez un ophtalmologiste adaptateur pres de chez vous."
        canonical="https://sfo-alc.fr/patients"
      />

      {/* Top utility bar — like reference site */}
      <div className="hidden md:flex bg-gradient-to-r from-[#04a4ec] via-[#1489f6] to-[#a3d977] items-center justify-between px-6 py-2 text-white text-xs" data-testid="patients-top-bar">
        <Link to="/" className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/20 hover:bg-white/30 backdrop-blur rounded-full text-[11px] font-semibold transition-colors" data-testid="link-praticiens">
          <ArrowRight size={11} className="rotate-180" /> Espace praticiens
        </Link>
        <div className="flex items-center gap-3">
          <span className="text-white/85 text-[11px]">Vous appréciez nos supports et vous désirez nous encourager :</span>
          <a href="https://www.helloasso.com/associations/sfoalc-societe-francaise-des-ophtalmologistes-adaptateurs-de-lentilles-de-contact"
            target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/95 text-[#045474] hover:bg-white rounded-full text-[11px] font-bold transition-colors shadow-sm" data-testid="link-donate-top">
            <Heart size={11} className="fill-[#ff6b9d] text-[#ff6b9d]" /> Soutenir la SFOALC
          </a>
        </div>
      </div>

      {/* Header */}
      <header className="bg-white/95 backdrop-blur-md border-b border-[#04a4ec]/10 sticky top-0 z-50 shadow-sm">
        <div className="max-w-6xl mx-auto px-6 py-0 flex items-center justify-between">
          <Link to="/patients" className="flex items-center gap-3 group py-3" data-testid="patients-logo">
            <img src="/SFOALC-logo-site.png" alt="SFOALC" className="h-9" />
            <div className="hidden sm:block leading-tight">
              <span className="text-base font-bold text-[#04a4ec]" style={{ fontFamily: "'Barlow', sans-serif" }}>Espace <span className="text-[#1d2746]">Patients</span></span>
            </div>
          </Link>
          {/* Nav links */}
          <nav className="hidden md:flex items-center gap-1">
            <Link to="/patients" className="px-4 py-2 text-sm font-semibold text-[#b5d37b] border-b-2 border-[#b5d37b] transition-colors" data-testid="nav-patient-home">Accueil</Link>
            {supportCards.map((card, i) => (
              <Link key={i}
                to={card.isFaq ? '/patients/faq' : `/patients/categorie/${card.slug}`}
                className="px-3 py-2 text-sm font-semibold text-[#1d2746] hover:text-[#04a4ec] transition-colors"
                data-testid={`nav-patient-${card.slug}`}>
                {card.title}
              </Link>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/annuaire"
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#1d2746] text-white rounded-full text-xs font-bold hover:bg-[#04a4ec] transition-all hover:shadow-md" data-testid="find-doctor-btn">
              <Search size={13} /> Annuaire des praticiens
            </Link>
          </div>
        </div>
      </header>

      {/* Hero — patient site style */}
      <section className="relative overflow-hidden" data-testid="patients-hero" style={{ minHeight: '480px' }}>
        {/* Background image with overlay */}
        <div className="absolute inset-0">
          <img src="/patient-hero-eye.jpg"
            alt=""
            className="w-full h-full object-cover"
            onError={(e) => { e.target.style.display = 'none'; }} />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(4,164,236,0.55) 0%, rgba(20,137,246,0.45) 50%, rgba(4,84,116,0.55) 100%)' }} />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 py-20 sm:py-28 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-3" style={{ fontFamily: "'Barlow', sans-serif", textShadow: '0 2px 16px rgba(4,84,116,0.5)' }} data-testid="patients-title">
            Vous avez besoin d'aide ?
          </h1>
          <p className="text-white/95 text-base sm:text-lg mb-10 drop-shadow">
            Retrouvez tous nos conseils & tutoriels librement
          </p>

          {/* Search bar */}
          <div className="max-w-2xl mx-auto relative" data-testid="patients-search">
            <input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Que recherchez-vous ?"
              className="w-full pl-7 pr-20 py-5 bg-white rounded-full text-sm border-0 shadow-2xl shadow-black/10 focus:outline-none focus:ring-2 focus:ring-[#b5d37b] transition-all"
            />
            <button
              onClick={() => { if (searchQuery) navigate(`/patients/search?q=${encodeURIComponent(searchQuery)}`); }}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-[#b5d37b] hover:bg-[#a3c167] flex items-center justify-center text-white transition-colors shadow-lg"
              aria-label="Rechercher"
              data-testid="patients-search-btn">
              <Search size={20} strokeWidth={2.5} />
            </button>
          </div>
        </div>

        {/* Wave curve at bottom */}
        <div className="absolute bottom-0 left-0 right-0 z-20">
          <svg viewBox="0 0 1000 80" preserveAspectRatio="none" className="block w-full" style={{ height: '50px' }}>
            <path fill="#ffffff" d="M0,80 L0,40 Q250,80 500,40 T1000,40 L1000,80 Z" />
          </svg>
        </div>
      </section>

      {/* Support cards */}
      <section className="px-4 py-16" data-testid="support-cards-section">
        <div className="max-w-6xl mx-auto">
          <p className="text-center text-xs font-bold uppercase tracking-[0.2em] text-[#04a4ec] mb-3">Nous sommes à votre écoute</p>
          <h2 className="text-center text-2xl sm:text-3xl font-bold text-[#1d2746] mb-10" style={{ fontFamily: "'Barlow', sans-serif" }}>Nos supports pour vous accompagner au quotidien</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {supportCards.map((card, i) => {
              const Icon = card.icon;
              return (
                <Link
                  key={i}
                  ref={el => cardsRef.current[i] = el}
                  data-card-index={i}
                  to={card.isFaq ? '/patients/faq' : `/patients/categorie/${card.slug}`}
                  className={`group relative bg-white rounded-2xl p-6 border border-gray-100 hover:border-[#04a4ec]/40 hover:shadow-xl transition-all duration-500 cursor-pointer text-center ${visibleCards.has(String(i)) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                  style={{ transitionDelay: `${i * 80}ms` }}
                  data-testid={`support-card-${i}`}
                >
                  <div className="w-20 h-20 rounded-full mx-auto mb-5 flex items-center justify-center transition-transform duration-300 group-hover:scale-110" style={{ background: card.bgLight }}>
                    <Icon size={32} style={{ color: card.color }} strokeWidth={2.2} />
                  </div>
                  <h3 className="text-base font-bold text-[#1d2746] mb-2 group-hover:text-[#04a4ec] transition-colors" style={{ fontFamily: "'Barlow', sans-serif" }}>{card.title}</h3>
                  <p className="text-gray-500 text-xs leading-relaxed mb-4 min-h-[60px]">{card.desc}</p>
                  <span className="inline-flex items-center gap-1.5 text-xs font-bold transition-all duration-300 group-hover:gap-2.5" style={{ color: card.color }}>
                    {card.cta} <ArrowRight size={12} className="transition-transform duration-300 group-hover:translate-x-0.5" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Annuaire CTA */}
      <section className="px-4 pb-16" data-testid="annuaire-cta">
        <div className="max-w-5xl mx-auto">
          <div className="relative rounded-3xl p-8 sm:p-12 text-white overflow-hidden group cursor-pointer hover:shadow-2xl transition-all duration-500"
            style={{ background: 'linear-gradient(135deg, #04a4ec 0%, #1489f6 60%, #045474 100%)' }}
            onClick={() => navigate('/annuaire')}>
            <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-white/10 -translate-y-1/2 translate-x-1/2 group-hover:scale-125 transition-transform duration-700" />
            <div className="relative z-10 flex items-center justify-between gap-8">
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Star size={16} className="text-[#b5d37b]" />
                  <span className="text-white/85 text-xs font-bold uppercase tracking-wider">L'annuaire des praticiens</span>
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold mb-2" style={{ fontFamily: "'Barlow', sans-serif" }}>Trouvez facilement un Ophtalmologiste</h3>
                <p className="text-white/85 text-sm sm:text-base">Adaptateur de lentilles de contact près de chez vous</p>
                <span className="inline-flex items-center gap-2 mt-4 px-5 py-2.5 bg-white text-[#04a4ec] rounded-full text-xs font-bold group-hover:bg-[#b5d37b] group-hover:text-white transition-colors">
                  Accéder à l'annuaire en ligne <ArrowRight size={13} />
                </span>
              </div>
              <div className="flex-shrink-0 hidden sm:block">
                <div className="w-14 h-14 rounded-full bg-white/15 flex items-center justify-center group-hover:bg-white/25 transition-colors duration-300">
                  <MapPin size={24} className="text-white" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Latest Articles */}
      {latestArticles.length > 0 && (
        <section className="px-4 pb-16" data-testid="latest-articles">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center" style={{ fontFamily: "'Barlow', sans-serif" }}>Derniers articles</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {latestArticles.map((article, i) => (
                <Link
                  key={article.id}
                  to={`/patients/article/${article.slug}`}
                  className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100"
                  data-testid={`article-card-${i}`}
                >
                  {article.featured_image && (
                    <div className="h-44 overflow-hidden">
                      <img src={article.featured_image} alt={article.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                    </div>
                  )}
                  <div className="p-5">
                    {article.category_name && <span className="inline-block px-3 py-1 bg-[#e7f5fd] text-[#04a4ec] text-[10px] font-bold rounded-full mb-3">{article.category_name}</span>}
                    <h3 className="text-base font-bold text-[#1d2746] mb-2 group-hover:text-[#04a4ec] transition-colors line-clamp-2" style={{ fontFamily: "'Barlow', sans-serif" }}>{article.title}</h3>
                    {article.excerpt && <p className="text-gray-500 text-xs line-clamp-2">{article.excerpt}</p>}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="bg-[#1d2746] text-white py-10" data-testid="patients-footer">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 mb-8">
            <div>
              <img src="/SFOALC-footer-logo.png" alt="SFOALC" className="h-12 mb-3" />
              <p className="text-white/60 text-xs leading-relaxed">
                Société Française des Ophtalmologistes Adaptateurs de Lentilles de Contact
              </p>
              <a href="mailto:contact@sfo-alc.fr" className="flex items-center gap-1.5 text-white/40 hover:text-white text-xs mt-3 transition-colors">
                <Mail size={12} /> contact@sfo-alc.fr
              </a>
            </div>
            <div>
              <h3 className="text-sm font-bold mb-3" style={{ fontFamily: "'Barlow', sans-serif" }}>Espace Patients</h3>
              <div className="space-y-1.5 text-xs">
                <Link to="/patients/categorie/conseils" className="flex items-center gap-1.5 text-white/50 hover:text-white transition-colors"><Heart size={13} className="opacity-50" /> Conseils d'utilisation</Link>
                <Link to="/patients/categorie/tutoriels-videos" className="flex items-center gap-1.5 text-white/50 hover:text-white transition-colors"><Video size={13} className="opacity-50" /> Tutoriels vidéos</Link>
                <Link to="/patients/faq" className="flex items-center gap-1.5 text-white/50 hover:text-white transition-colors"><HelpCircle size={13} className="opacity-50" /> Foire aux questions</Link>
                <Link to="/patients/categorie/en-savoir-plus" className="flex items-center gap-1.5 text-white/50 hover:text-white transition-colors"><BookOpen size={13} className="opacity-50" /> En savoir plus</Link>
              </div>
            </div>
            <div>
              <h3 className="text-sm font-bold mb-3" style={{ fontFamily: "'Barlow', sans-serif" }}>L'association</h3>
              <div className="space-y-1.5 text-xs mb-4">
                <Link to="/annuaire" className="flex items-center gap-1.5 text-white/50 hover:text-white transition-colors"><MapPin size={13} className="opacity-50" /> Annuaire des praticiens</Link>
                <Link to="/" className="flex items-center gap-1.5 text-white/50 hover:text-white transition-colors"><Globe size={13} className="opacity-50" /> Site principal</Link>
                <Link to="/contact" className="flex items-center gap-1.5 text-white/50 hover:text-white transition-colors"><Phone size={13} className="opacity-50" /> Contact</Link>
              </div>

              {/* Donate heart button */}
              <a href="https://www.helloasso.com/associations/sfoalc-societe-francaise-des-ophtalmologistes-adaptateurs-de-lentilles-de-contact" target="_blank" rel="noopener noreferrer"
                className="group flex items-center gap-2.5 mb-5">
                <div className="relative flex items-center justify-center w-8 h-8">
                  <span className="absolute inset-0 rounded-full bg-rose-500/20 animate-ping" style={{ animationDuration: '2s' }} />
                  <span className="absolute inset-0.5 rounded-full bg-rose-500/10 animate-ping" style={{ animationDuration: '2.5s', animationDelay: '0.3s' }} />
                  <div className="relative w-8 h-8 rounded-full bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center shadow-lg shadow-rose-500/30 group-hover:scale-110 transition-transform">
                    <Heart size={14} className="text-white fill-white" />
                  </div>
                </div>
                <div>
                  <p className="text-rose-300 text-xs font-bold group-hover:text-rose-200 transition-colors">Soutenir l'association</p>
                  <p className="text-white/40 text-[10px]">Faire un don</p>
                </div>
              </a>

              {/* PWA Install — animated icon */}
              {showPWA && (
                <button onClick={handleInstall} className="group w-full" data-testid="patient-pwa-install-btn">
                  <div className={`flex items-center gap-3 px-4 py-3 rounded-full border-2 transition-all duration-500 ${installing ? 'border-[#35AFE4] bg-[#35AFE4]/20' : 'border-[#35AFE4]/30 bg-[#35AFE4]/5 hover:border-[#35AFE4]/70 hover:bg-[#35AFE4]/15'}`}>
                    <div className={`w-9 h-9 rounded-full bg-[#35AFE4] flex items-center justify-center flex-shrink-0 transition-all duration-500 ${installing ? 'scale-90' : 'group-hover:scale-110 group-hover:shadow-[0_0_16px_rgba(58,143,183,0.5)]'}`}>
                      <Download size={16} className="text-white animate-[downloadBounce_2s_ease-in-out_infinite]" />
                    </div>
                    <div className="text-left min-w-0">
                      <p className="text-white text-[13px] font-bold leading-tight">{installing ? 'Installation...' : "Installer l'app"}</p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <Monitor size={11} className="text-white/50" />
                        <Tablet size={11} className="text-white/50" />
                        <Smartphone size={11} className="text-white/50" />
                        <span className="text-white/50 text-[11px]">iOS / Android / PC</span>
                      </div>
                    </div>
                  </div>
                </button>
              )}
            </div>
          </div>
          <div className="border-t border-white/10 pt-4 flex flex-col sm:flex-row justify-between items-center gap-2">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-white/30 text-[10px]">
              <span>&copy; {new Date().getFullYear()} SFOALC</span>
              <Link to="/legal" className="hover:text-white/60 transition-colors">Mentions légales</Link>
              <span>|</span>
              <Link to="/privacy" className="hover:text-white/60 transition-colors">Confidentialité</Link>
            </div>
            <a href="https://agence-msc.fr" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 group">
              <span className="text-white/30 text-[10px] group-hover:text-white/50 transition-colors">Site et application développés par</span>
              <img src="/logo-agence-msc.webp" alt="Agence MSC" className="h-3.5 w-auto opacity-50 group-hover:opacity-90 transition-opacity" />
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PatientsHomePage;
